import React, { useState, useCallback, useEffect, useRef } from "react";
import "./SearchBar.scss";
import { Content } from "antd/es/layout/layout";
import { theme, AutoComplete, Input, Empty, notification, Button } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { AppTitle } from "../../../../components";
import { useNavigate } from "react-router-dom";
import useWindowDimensions from "../../../../hooks/useWindowDimensions";
import { useDispatch, useSelector } from "react-redux";
import { getPatients } from "../../../../features/patients/patientsActions";
import { resetPatientsError } from "../../../../features/patients/patientsSlice";
import LocalStorageService from "../../../../common/services/LocalStorageService";
import Utils from "../../../../utils";
import { SignIn } from "../../../../modals";

export default function SearchBar() {
  const { useToken } = theme;
  const { token } = useToken();
  const [api, contextHolder] = notification.useNotification();
  const openNotificationWithIcon = useCallback(
    (type, title, text) => {
      api[type]({
        message: title,
        description: text,
        placement: "top",
      });
    },
    [api]
  );
  const { isAuthenticated } = useSelector((state) => state?.auth);
  const navigate = useNavigate();
  const { isMobile } = useWindowDimensions();
  const dispatch = useDispatch();
  const {
    patientsData: { results: patients },
    error,
    loading,
  } = useSelector((state) => state?.patients);
  const inputRef = useRef(null);
  const [is_signin_opened, set_is_signin_opened] = useState(false);

  const is_search_limit_reached =
    LocalStorageService?.get("search_count") >= 7 && !isAuthenticated;

  const navigateToPatients = (params) => {
    if (is_search_limit_reached) {
      set_is_signin_opened(true);
      return;
    }
    const searchParams = new URLSearchParams();
    if (params.search) searchParams.set("search", params.search);
    searchParams.set("page", params.page || 1);
    searchParams.set("per_page", params.per_page || 10);
    navigate(`/patients?${searchParams.toString()}`);
  };

  const onSearch = Utils?.debounce((val, cancelDebounce) => {
    if (!cancelDebounce) {
      dispatch(
        getPatients({
          search: val ? val?.toLowerCase() : undefined,
          is_active: true,
        })
      );
    }
  }, 500);

  const onSelect = (_, option) => {
    if (is_search_limit_reached) {
      set_is_signin_opened(true);
      return;
    }
    navigateToPatients({
      search: option?.label ? option?.label : undefined,
      page: 1,
      per_page: 10,
    });
    if (inputRef?.current?.input?.value) {
      onSearch("", true);
    }
  };

  useEffect(() => {
    dispatch(
      getPatients({
        search: inputRef?.current?.input?.value
          ? inputRef?.current?.input?.value
          : undefined,
          is_active: true,
      })
    );
  }, [dispatch, isAuthenticated]);

  useEffect(() => {
    if (error && !loading) {
      if (typeof error === "object" && !Array.isArray(error)) {
        for (let key in error) {
          if (typeof key === "string" && typeof error[key] === "string") {
            openNotificationWithIcon("error", key, error[key]);
            break;
          }
        }
      } else {
        openNotificationWithIcon("error", "Error", "Something went wrong !");
      }
      dispatch(resetPatientsError());
    }
  }, [error, loading, openNotificationWithIcon, dispatch]);

  return (
    <>
      {contextHolder}
      <Content className="searchBar-cont">
        <div className="searchBar-cont__content">
          <AppTitle
            token={token}
            classNameCont="searchBar-cont__content__titeWrapper"
            classNameTitle="searchBar-cont__title"
          />
          <AutoComplete
            className="searchBar-cont__search"
            style={{ height: isMobile ? "32px" : "40px" }}
            disabled={is_search_limit_reached}
            options={Utils?.madeUnique(patients)?.map((patient) => ({
              label: `${patient?.first_name ? patient?.first_name : ""} ${
                patient?.last_name ? patient?.last_name : ""
              }`,
              value: `${patient?.first_name ? patient?.first_name : ""} ${
                patient?.last_name ? patient?.last_name : ""
              }`,
              key: `${patient?.id}`,
            }))}
            onSearch={onSearch}
            onSelect={onSelect}
            notFoundContent={<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />}>
            <Input
              style={{
                borderRadius: "24px",
                height: isMobile ? "32px" : "40px",
              }}
              ref={inputRef}
              size={isMobile ? "middle" : "large"}
              placeholder="Search"
              prefix={<SearchOutlined />}
              allowClear
              onPressEnter={(e) => {
                const value = e?.target?.value;
                if (
                  (e?.key === "Enter" || e?.code === "NumpadEnter") &&
                  value
                ) {
                  if (is_search_limit_reached) {
                    set_is_signin_opened(true);
                    return;
                  }
                  onSearch("", true);
                  navigateToPatients({
                    search: value ? value : undefined,
                    page: 1,
                    per_page: 10,
                  });
                }
              }}
            />
          </AutoComplete>
          <Button
            className="searchBar-cont__quickSearch"
            size={isMobile ? "middle" : "large"}
            disabled={is_search_limit_reached}
            shape="round"
            icon={<SearchOutlined />}
            onClick={() => {
              if (is_search_limit_reached) {
                set_is_signin_opened(true);
                return;
              }
              navigateToPatients({
                search: inputRef?.current?.input?.value
                  ? inputRef?.current?.input?.value
                  : undefined,
                page: 1,
                per_page: 10,
              });
              if (inputRef?.current?.input?.value) {
                onSearch("", true);
              }
            }}>
            Quick Search
          </Button>
        </div>
      </Content>
      <SignIn
        token={token}
        isOpened={is_signin_opened}
        onCancel={() => {
          set_is_signin_opened(false);
        }}
      />
    </>
  );
}
